<template>
  <RfBaseModal
    ref="modalRef"
    :title="`Assign Segment Traffic: ${record?.name}`"
    width="640"
    secondaryText="Assign Traffic"
    @secondary="submitAssignTraffic"
  >
    <template #body>
      <div v-if="record" class="flex flex-col gap-6 px-1">
        <div>
          You can limit this {{ recordType }} to be active for only a portion of user traffic in
          this segment. This can be useful to experiment between prompt types. For example, if you
          set the slider to 0-50, and another prompt to 50-100, then half of users in this segment
          will see this {{ recordType }}, and the remainder will see the other prompt.
        </div>
        <div v-if="record.segments.length > 1" class="bg-goal -mt-2 mb-2 rounded px-5 py-4">
          <v-icon class="mr-1 !align-baseline" size="14" color="warning">
            fa:fas fa-exclamation-triangle
          </v-icon>
          This {{ recordType }} is in multiple segments, and will be shown to users of ANY of the
          segments. We recommend experimenting between prompts in a single segment only, or ensuring
          the segments are mutually exclusive.
        </div>
        <p class="text-black-2 mb-8 font-semibold">
          <span class="capitalize">{{ recordType }}</span> will be active for
          {{ range[1] - range[0] }}% of segment traffic:
        </p>
        <v-range-slider
          v-model="range"
          strict
          min="0"
          step="1"
          max="100"
          thumbLabel="always"
          class="[&_.v-slider-track\_\_background]:bg-background! [&_.v-slider-thumb\_\_label]:bg-black-1/80! [&_.v-slider-thumb\_\_surface]:bg-white-1! [&_.v-slider-thumb\_\_surface]:before:border-strokes! [&_.v-slider-thumb\_\_label]:font-bold! [&_.v-slider-thumb\_\_label-container]:-mt-3! [&_.v-slider-thumb\_\_ripple]:hidden! [&_.v-slider-thumb\_\_surface]:flex! [&_.v-slider-thumb\_\_surface]:h-5! [&_.v-slider-thumb\_\_surface]:w-4! [&_.v-slider-thumb\_\_surface]:cursor-pointer! [&_.v-slider-thumb\_\_surface]:items-center! [&_.v-slider-thumb\_\_surface]:justify-center! [&_.v-slider-thumb\_\_surface]:gap-1! [&_.v-slider-thumb\_\_surface]:rounded! [&_.v-slider-thumb\_\_surface]:shadow-sm! [&_.v-slider-thumb\_\_surface]:before:static! [&_.v-slider-thumb\_\_surface]:before:h-3! [&_.v-slider-thumb\_\_surface]:before:w-2! [&_.v-slider-thumb\_\_surface]:before:transform-none! [&_.v-slider-thumb\_\_surface]:before:rounded-none! [&_.v-slider-thumb\_\_surface]:before:border-x-2! [&_.v-slider-thumb\_\_surface]:before:border-solid! [&_.v-slider-thumb\_\_surface]:before:bg-transparent! [&_.v-slider-thumb\_\_surface]:before:opacity-100! [&_.v-slider-track\_\_background]:h-10! [&_.v-slider-track\_\_background]:overflow-hidden! [&_.v-slider-track\_\_background]:rounded! [&_.v-slider-track\_\_fill]:h-10! [&_.v-slider-track\_\_fill]:overflow-hidden! [&_.v-slider-track\_\_fill]:rounded! [&_.v-slider-track\_\_fill]:bg-blue-50! [&_.v-slider\_\_container]:mx-0!"
        />
        <RfSegmentBucketRangeBar
          class="pointer-events-none z-10 -mt-20 mb-4 h-10 font-bold"
          activeClass="text-white-1"
          inactiveClass="text-grey-3"
          :range="range"
        />
      </div>
    </template>
  </RfBaseModal>
</template>

<script setup>
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfSegmentBucketRangeBar from "@/components/RfSegments/RfSegmentBucketRangeBar.vue";
import { segmentItemFor, displayBucketRange } from "@/utils/segmentsHelpers";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { usePromptStore } from "@/pinia/promptStore";
import { useSequencesStore } from "@/pinia/sequencesStore";
import { useToastsStore } from "@/pinia/toastsStore";

const route = useRoute();
const promptStore = usePromptStore();
const sequencesStore = useSequencesStore();
const toastsStore = useToastsStore();

const props = defineProps(["segment"]);
const emit = defineEmits(["assignTraffic"]);

const modalRef = ref();
const record = ref();
const range = ref([]);

const recordType = computed(() => (record.value.path_type ? "prompt" : "guide"));
const segmentItem = computed(() => segmentItemFor(props.segment, record.value));

const show = promptOrGuide => {
  record.value = promptOrGuide;
  range.value = displayBucketRange(segmentItem.value);
  modalRef.value.show();
};
const close = () => modalRef.value.close();

const submitAssignTraffic = async () => {
  const appId = route.params.aid;
  segmentItem.value.options.user_bucket.range = [range.value[0] * 100, range.value[1] * 100 - 1];
  if (recordType.value === "prompt") {
    await promptStore.updatePrompt({
      appId,
      pathId: record.value.id,
      modelPath: record.value,
    });
  } else {
    await sequencesStore.updateSequence({
      appId,
      sequenceId: record.value.id,
      modelSequence: record.value,
    });
  }
  toastsStore.create({ type: "success", body: `Traffic assigned for: ${record.value.name}` });
  close();
};

defineExpose({ show, close });
</script>
